/**
 * CreatorBriefDetail
 *
 * Full-page detail view for a single brief, reached at
 * ``/creator/:clientId/briefs/:briefId``. A single mobile-first column of
 * WorkspacePanels (DD-002/DD-011) mirroring the admin brief cockpit:
 * Brief (title, status badge, context, events), shot list, linked ideas,
 * and notes. A back link returns to the brief list. All data is fetched
 * via the portal HttpClient (JWT scopes the client). Uploads reuse
 * ``UploadMediaModal`` which already understands portal auth via
 * ``RoleProvider``.
 *
 * User-facing copy still references the photographer role since that is
 * the only creator type live today; the video editor role will land here
 * later and swap copy per role.
 */

import React, { useState } from "react";
import { useDetailTrail } from "@/core/navigation/TrailDisplayContext";
import { useLocale } from "@/hooks/useLocale";
import {
  useBriefQuery,
  useAddBriefNoteMutation,
} from "@/domains/Briefs/hooks/useBriefs";
import { BriefStatusBadge } from "@/domains/Briefs/components/BriefStatusBadge";
import { UploadMediaModal } from "@/domains/LibraryView/components/UploadMediaModal";
import { RoleProvider } from "@/contexts/RoleContext";
import { UserRole } from "@/services/urlService";
import { SpinnerIcon, UploadIcon, PlusIcon } from "@/components/icons";
import { IdeaCard } from "@/domains/Ideas/components/IdeaCard";
import { Idea } from "@/domains/Ideas/types";
import { ideasService } from "@/domains/Ideas/services/ideasService";
import { useHttpClient } from "@/core/http/HttpClientContext";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { CreatorIdeaDetailModal } from "./CreatorIdeaDetailModal";
import { CalendarEventCard } from "@/domains/Home/components/CalendarEventCard";
import { TaskListEditor } from "@/domains/Tasks/components/TaskListEditor";
import { TaskDrawer } from "@/domains/Tasks/drawer/TaskDrawer";
import { useTaskDrawer } from "@/domains/Tasks/drawer/useTaskDrawer";
import { capabilitiesFor } from "@/domains/Tasks/taskCapabilities";
import { taskListProgress } from "@/domains/Tasks/taskList";
import { EMPTY_TASK_LIST } from "@/domains/Tasks/types";
import {
  useShotListQuery,
  useToggleTask,
} from "@/domains/Tasks/hooks/useTasks";

interface CreatorBriefDetailProps {
  clientId: string;
  briefId: string;
}

type PanelId = "brief" | "shots" | "ideas" | "notes";

export const CreatorBriefDetail: React.FC<CreatorBriefDetailProps> = ({
  clientId,
  briefId,
}) => {
  const { t } = useLocale();
  const http = useHttpClient();
  const { data: brief, isLoading, error } = useBriefQuery(briefId);
  // Publishes the header breadcrumb (Brief › <title>); deep links get the
  // canonical list as fallback.
  useDetailTrail({
    fallback: { to: `/creator/${clientId}`, label: t("creator.tabs.brief") },
    title: brief?.title || t("creator.brief.untitled"),
  });
  const [noteContent, setNoteContent] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
  const [ideaUploadCollectionId, setIdeaUploadCollectionId] =
    useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<PanelId, boolean>>({
    brief: false,
    shots: false,
    ideas: false,
    notes: false,
  });
  const toggle = (id: PanelId) =>
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const handleIdeaEdit = (idea: Idea) => setSelectedIdeaId(idea.id);

  const handleIdeaUpload = async (ideaId: string) => {
    try {
      const collectionId = await ideasService.ensureIdeaCollection(http, ideaId);
      setIdeaUploadCollectionId(collectionId);
    } catch (err) {
      console.error("Failed to prepare idea upload:", err);
    }
  };

  // Hook is called unconditionally; the effective brief id is resolved at
  // mutation time via the closure re-created whenever the brief changes.
  const addNote = useAddBriefNoteMutation(brief?.id ?? "");
  const { data: shotList } = useShotListQuery(brief?.id);
  const toggleTask = useToggleTask();
  const taskDrawer = useTaskDrawer();
  const creatorCapabilities = capabilitiesFor("creator");

  const shotProgress = taskListProgress(shotList ?? EMPTY_TASK_LIST);
  const ideaCount = brief?.ideas?.length ?? 0;
  const noteCount = brief?.notes?.length ?? 0;

  const handleAddNote = async () => {
    const content = noteContent.trim();
    if (!content || !brief) return;
    // Tag preserves the historical "photographer" semantic so existing
    // backend filters/dashboards keep working. Update when we introduce a
    // role discriminator.
    await addNote.mutateAsync({ content, tags: ["photographer"] });
    setNoteContent("");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerIcon className="w-6 h-6 text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-white p-4 text-sm text-red-600">
        {t("creator.brief.loadError")}
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="rounded-2xl bg-white p-8 text-center text-sm text-gray-400">
        {t("creator.brief.empty")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Brief — title, status, distilled context and attached events,
          mirroring the admin Concept panel (DD-010/DD-014). */}
      <WorkspacePanel
        title={t("creator.tabs.brief")}
        icon={<ENTITY_CONFIG.brief.icon className="w-4 h-4" />}
        collapsed={collapsed.brief}
        onToggleCollapse={() => toggle("brief")}
        actions={
          <PanelHeaderAction
            label={t("creator.brief.upload")}
            icon={<UploadIcon className="w-4 h-4" />}
            onClick={() => setShowUpload(true)}
          />
        }
        subheader={
          <div className="space-y-3 pt-1">
            <h2 className="text-xl font-bold tracking-tight text-gray-900 leading-tight">
              {brief.title || t("creator.brief.untitled")}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <BriefStatusBadge status={brief.status} />
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          {brief.distilledContext && (
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {brief.distilledContext}
            </div>
          )}

          {brief.calendarEvents && brief.calendarEvents.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em] mb-2">
                {t("creator.brief.events")}
              </div>
              <div className="flex flex-col gap-1">
                {brief.calendarEvents.map((event) => (
                  <CalendarEventCard
                    key={event.id}
                    variant="compact"
                    event={event}
                  />
                ))}
              </div>
            </div>
          )}

          {!brief.distilledContext &&
            (!brief.calendarEvents || brief.calendarEvents.length === 0) && (
              <p className="text-sm text-gray-400">
                {t("creator.brief.contextEmpty")}
              </p>
            )}
        </div>
      </WorkspacePanel>

      {/* Shot list — the shared task list (DD-052); a row opens a view-only
          drawer (DD-017). */}
      {shotProgress.total > 0 && (
        <WorkspacePanel
          title={t("creator.brief.shotList")}
          icon={<ENTITY_CONFIG.task.icon className="w-4 h-4" />}
          summary={`${shotProgress.done}/${shotProgress.total}`}
          collapsed={collapsed.shots}
          onToggleCollapse={() => toggle("shots")}
        >
          <TaskListEditor
            list={shotList ?? EMPTY_TASK_LIST}
            capabilities={creatorCapabilities}
            onToggle={toggleTask}
            onOpen={taskDrawer.open}
          />
        </WorkspacePanel>
      )}

      {/* Linked ideas — IdeaCard rows with the creator affordances. */}
      <WorkspacePanel
        title={t("creator.brief.ideas")}
        icon={<ENTITY_CONFIG.idea.icon className="w-4 h-4" />}
        summary={ideaCount > 0 ? ideaCount : undefined}
        collapsed={collapsed.ideas}
        onToggleCollapse={() => toggle("ideas")}
      >
        {brief.ideas && brief.ideas.length > 0 ? (
          <ul className="space-y-3">
            {brief.ideas.map((idea) => (
              <li key={idea.id}>
                <IdeaCard
                  idea={idea}
                  viewMode="creator"
                  onEdit={handleIdeaEdit}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-3 text-sm text-gray-400">
            {t("creator.brief.ideasEmpty")}
          </p>
        )}
      </WorkspacePanel>

      {/* Notes — flat list plus composer. */}
      <WorkspacePanel
        title={t("creator.brief.notes")}
        icon={<ENTITY_CONFIG.note.icon className="w-4 h-4" />}
        summary={noteCount > 0 ? noteCount : undefined}
        collapsed={collapsed.notes}
        onToggleCollapse={() => toggle("notes")}
      >
        <div className="space-y-4">
          {brief.notes && brief.notes.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {brief.notes.map((note) => (
                <li key={note.id} className="py-3 text-sm text-gray-700">
                  <p className="whitespace-pre-wrap">{note.content}</p>
                  {note.tags && note.tags.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {note.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">
              {t("creator.brief.notesEmpty")}
            </p>
          )}

          <div className="flex gap-2">
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder={t("creator.brief.notePlaceholder")}
              rows={2}
              className="flex-1 px-3 py-2 text-sm bg-gray-50 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={handleAddNote}
              disabled={!noteContent.trim() || addNote.isPending}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 self-start transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              {t("creator.brief.addNote")}
            </button>
          </div>
        </div>
      </WorkspacePanel>

      <RoleProvider role={UserRole.CLIENT} clientId={clientId}>
        <UploadMediaModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          clientId={clientId}
          contentType="auto"
          onUploadComplete={() => setShowUpload(false)}
        />
        <UploadMediaModal
          isOpen={!!ideaUploadCollectionId}
          onClose={() => setIdeaUploadCollectionId(null)}
          clientId={clientId}
          contentType="auto"
          collectionId={ideaUploadCollectionId ?? undefined}
          onUploadComplete={() => setIdeaUploadCollectionId(null)}
        />
      </RoleProvider>

      <TaskDrawer drawer={taskDrawer} capabilities={creatorCapabilities} />

      <CreatorIdeaDetailModal
        ideaId={selectedIdeaId}
        clientId={clientId}
        isOpen={!!selectedIdeaId}
        onClose={() => setSelectedIdeaId(null)}
        onUpload={(id) => void handleIdeaUpload(id)}
      />
    </div>
  );
};
